/**
 * Store de administración con Zustand
 * Sprint 7 - HU-FE-03: Estado global del panel admin
 */

import { create } from 'zustand';
import { adminService } from '../services/admin';
import type {
    AdminUser,
    DashboardStats,
    MapUser,
    PaginationMeta,
    ListUsersParams,
    MapUsersParams,
    CreateAdminData,
    UserDetailResponse,
} from '../types';

interface AdminState {
    // Estado
    users: AdminUser[];
    stats: DashboardStats | null;
    mapUsers: MapUser[];
    selectedUser: UserDetailResponse['data'] | null;
    pagination: PaginationMeta | null;
    isLoading: boolean;
    error: string | null;

    // Filtros
    filters: ListUsersParams;

    // Acciones
    fetchStats: () => Promise<void>;
    fetchUsers: (params?: ListUsersParams) => Promise<void>;
    fetchUserDetail: (id: number) => Promise<void>;
    fetchMapUsers: (params?: MapUsersParams) => Promise<void>;
    createAdmin: (data: CreateAdminData) => Promise<boolean>;
    setFilters: (filters: Partial<ListUsersParams>) => void;
    clearError: () => void;
    clearSelectedUser: () => void;
}

export const useAdminStore = create<AdminState>((set, get) => ({
    // Estado inicial
    users: [],
    stats: null,
    mapUsers: [],
    selectedUser: null,
    pagination: null,
    isLoading: false,
    error: null,
    filters: {},

    fetchStats: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await adminService.getDashboardStats();
            set({ stats: response.data, isLoading: false });
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Error al cargar estadísticas';
            set({ error: message, isLoading: false });
        }
    },

    fetchUsers: async (params?: ListUsersParams) => {
        const mergedParams = { ...get().filters, ...params };
        set({ isLoading: true, error: null });
        try {
            const response = await adminService.getUsers(mergedParams);
            set({
                users: response.data,
                pagination: response.meta,
                isLoading: false,
            });
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Error al cargar usuarios';
            set({ error: message, isLoading: false });
        }
    },

    fetchUserDetail: async (id: number) => {
        set({ isLoading: true, error: null });
        try {
            const response = await adminService.getUserDetail(id);
            set({ selectedUser: response.data, isLoading: false });
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Error al cargar detalle del usuario';
            set({ error: message, isLoading: false });
        }
    },

    fetchMapUsers: async (params?: MapUsersParams) => {
        set({ isLoading: true, error: null });
        try {
            const response = await adminService.getMapUsers(params);
            set({ mapUsers: response.data, isLoading: false });
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Error al cargar mapa de usuarios';
            set({ error: message, isLoading: false });
        }
    },

    createAdmin: async (data: CreateAdminData) => {
        set({ isLoading: true, error: null });
        try {
            await adminService.createAdmin(data);
            set({ isLoading: false });
            return true;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Error al crear administrador';
            set({ error: message, isLoading: false });
            return false;
        }
    },

    setFilters: (filters: Partial<ListUsersParams>) => {
        set((state) => ({
            filters: { ...state.filters, ...filters },
        }));
    },

    clearError: () => set({ error: null }),
    clearSelectedUser: () => set({ selectedUser: null }),
}));
